import React from 'react';
import { curvedWoodHoodLayout } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { Stroke } from './VentHoodChimneyViews';

/**
 * Venus's curved wood hood (WRH) on paper — the 2D twin of
 * `curvedWoodHoodParts.buildCurvedWoodHood`, every number from
 * `curvedWoodHoodLayout`: the chimney, the flare sweeping out from it, the
 * base band and its two moldings standing proud. Svg y runs down from the
 * object box's top.
 */

// points down each flaring edge
const CURVE_STEPS = 16;

const svgFrame = (w, h) => ({
  viewBox: `0 0 ${w} ${h}`,
  preserveAspectRatio: 'xMidYMid meet',
  style: { width: '100%', height: '100%', overflow: 'visible' },
});

const rect = (x0, x1, y0, y1) => `M ${x0} ${y0} H ${x1} V ${y1} H ${x0} Z`;

/**
 * A flaring edge from the chimney's foot to the band's top: `xAt(at)` is its
 * x for the flare's outline `at` (`flareAt`) at each step down.
 */
function flareEdge(L, xAt) {
  const pts = Array.from({ length: CURVE_STEPS + 1 }, (_, i) => {
    const s = i / CURVE_STEPS;
    return [xAt(L.flareAt(s)), L.straight + s * L.flareHeight];
  });
  return pts.map(([x, y], i) => `${i ? 'L' : 'M'} ${x} ${y}`).join(' ');
}

/** The band across `x0`–`x1`, its moldings across `proudX(proud)`. */
function BandAndMoldings({ L, height, x0, x1, proudX, ...stroke }) {
  const bandTop = height - L.band;
  return (
    <>
      <Stroke
        data-part="wood-hood-band"
        d={rect(x0, x1, bandTop, height)}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-cap-molding"
        d={rect(...proudX(L.cap.proud), bandTop, bandTop + L.cap.height)}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-base-molding"
        d={rect(...proudX(L.base.proud), height - L.base.height, height)}
        {...stroke}
      />
    </>
  );
}

/** The front: the chimney centred over the flare, the band full width. */
export function CurvedWoodHoodFrontView({ width, height, depth, ...stroke }) {
  const L = curvedWoodHoodLayout({ width, height, depth });
  const mid = width / 2;
  const c0 = mid - L.chimneyWidth / 2;
  return (
    <svg {...svgFrame(width, height)}>
      <Stroke
        data-part="wood-hood-chimney"
        d={rect(c0, c0 + L.chimneyWidth, 0, L.straight)}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-flare"
        d={`${flareEdge(L, (at) => mid - at.width / 2)} ${flareEdge(
          L,
          (at) => mid + at.width / 2
        )}`}
        {...stroke}
      />
      <BandAndMoldings
        L={L}
        height={height}
        x0={0}
        x1={width}
        proudX={(p) => [-p, width + p]}
        {...stroke}
      />
    </svg>
  );
}

/**
 * The side: the back on the wall, the chimney 12" deep, the front flaring
 * out to the band, the moldings proud of the front — x = 0 is the front on
 * a LEFT view; a RIGHT view is its mirror.
 */
export function CurvedWoodHoodSideView({
  width,
  height,
  depth,
  view,
  ...stroke
}) {
  const L = curvedWoodHoodLayout({ width, height, depth });
  const profile = (
    <g>
      <Stroke
        data-part="wood-hood-chimney"
        d={rect(depth - L.chimneyDepth, depth, 0, L.straight)}
        {...stroke}
      />
      <Stroke
        data-part="wood-hood-flare"
        d={`${flareEdge(L, (at) => depth - at.depth)} M ${depth} ${L.straight} V ${height - L.band}`}
        {...stroke}
      />
      <BandAndMoldings
        L={L}
        height={height}
        x0={0}
        x1={depth}
        proudX={(p) => [-p, depth]}
        {...stroke}
      />
    </g>
  );
  return (
    <svg {...svgFrame(depth, height)}>
      {view === 'right' ? (
        <g transform={`translate(${depth}, 0) scale(-1, 1)`}>{profile}</g>
      ) : (
        profile
      )}
    </svg>
  );
}
